import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import { DoctorSearchDropdown } from 'components/patient/upsert/DoctorSearchDropdown';
import ReportDiagnosisComponent from 'components/patient/upsert/ReportDiagnosisComponent';
import PatientReportPdfModal from 'components/pdf-modal/PatientReportPdfModal';
import { FormApi, FormState } from 'final-form';
import arrayMutators from 'final-form-arrays';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Message, Modal, RadioProps } from 'semantic-ui-react';
import { getPdfDocumentUrl } from 'services/patientRecordServices';
import { generatePatientPhysiotherapyReport } from 'services/patientServices';
import { debounce } from 'ts-debounce';
import {
  PhysiotherapyTreatmentGoal,
  PhysiotherapyTreatmentPrescription,
  PhysiotherapyTreatmentsNumber
} from 'ts-types/api.enums';
import {
  DoctorDto,
  PatientDto,
  PatientPhysiotherapyReportDto,
  PatientReportDto,
  PdfResponse,
  ReportDiagnosisDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { generateUUID } from 'utils/generatorUtils';


const addButton = {
  marginBottom: 10,
  marginTop: 10
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  modalOpen: boolean;
  onCloseModal: (savedDocument: boolean) => void;
  patient: PatientDto;
  treatmentId: number;
  reportDetails?: PatientPhysiotherapyReportDto;
  documentId?: number;
}

interface PatientPhysiotherapyReport extends PatientPhysiotherapyReportDto {
  doctorInput: string | number | undefined,
  selectedTreatmentGoals: { [key in PhysiotherapyTreatmentGoal]: boolean }
}

interface PatientPhysiotherapyReportFieldProps extends FormState {
  values: Partial<PatientPhysiotherapyReport>
}

export interface ReportDiagnosisDtoWithId extends ReportDiagnosisDto {
  id: string
}

const UpsertPhysiotherapyReportModal = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const {
    modalOpen,
    onCloseModal,
    patient,
    treatmentId,
    reportDetails,
    documentId,
    options,
    getMessage
  } = props;

  const [doctor, setDoctor] = useState(
      undefined as DoctorDto | string | undefined);

  const [doctorEmail, setDoctorEmail] = useState("");

  const [diagnoses, setDiagnoses] = useState(
      [] as ReportDiagnosisDtoWithId[]);

  const [selectedDiagnoses, setSelectedDiagnoses] = useState(
      [] as ReportDiagnosisDtoWithId[]);

  const [addDiagnose, setAddDiagnose] = useState(false);
  const [addDiagnoseIx, setAddDiagnoseIx] = useState(undefined as number | undefined);

  const [treatmentPrescription, setTreatmentPrescription] = useState(
      [] as RadioProps[]);

  const [treatmentsNumber, setTreatmentsNumber] = useState(
      [] as RadioProps[]);

  const [reportDto, setReportDto] = useState(undefined as PatientReportDto | undefined);

  const [patientPhysiotherapyReportModalOpen, setPatientPhysiotherapyReportModalOpen] = useState(false);
  const [patientPhysiotherapyReportPdfResponse, setPatientPhysiotherapyReportPdfResponse]
      = useState(undefined as PdfResponse | undefined);

  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);
  const [successfullySavedDocument, setSuccessfullySavedDocument] = useState(undefined as string | undefined);
  const [successfullySendEmail, setSuccessfullySendEmail] = useState(undefined as string | undefined);

  const [requiredDataLoaded, setRequiredDataLoaded] = useState(false);

  const initialValues = useRef<Partial<PatientPhysiotherapyReport>>({});

  useEffect(() => {

    initialValues.current = {
      reportName: "Physiotherapy"
    }

    if (reportDetails) {
      const currentDiagnoses = reportDetails.diagnosis || [];
      const mappedDiagnoses = currentDiagnoses.map(diagnosis => ({
        ...diagnosis,
        id: generateUUID()
      }));

      initialValues.current = {
        ...reportDetails,
        reportName: reportDetails.reportName,
        diagnosis: mappedDiagnoses,
        doctorInput: reportDetails.doctor
      };

      const goals =
          _.mapValues(
              _.keyBy(
                  Object.values(PhysiotherapyTreatmentGoal)),
              goal => !reportDetails.treatmentGoals || reportDetails.treatmentGoals!.includes(goal));
      initialValues.current.selectedTreatmentGoals = goals as any;

      setDiagnoses(mappedDiagnoses);
    }

    const treatmentPrescriptionLabels: string[] = Object.values(PhysiotherapyTreatmentPrescription);
    setTreatmentPrescription(generateRadioButtonOptionsFromEnum(
        treatmentPrescriptionLabels, "enum.physiotherapyPrescription", getMessage));

    const treatmentsNumberLabels: string[] = Object.values(PhysiotherapyTreatmentsNumber);
    setTreatmentsNumber(generateRadioButtonOptionsFromEnum(
        treatmentsNumberLabels, "enum.treatmentsNumber", getMessage));

    const fetchData = async () => {
      try {

        // await getDiagnosisForReport(patient.id, cancelTokenSource)
        //     .then(response => {
        //       initialValues = {
        //         diagnosis: response
        //       }
        //       setDiagnoses(response);
        //     })
        //     .catch((e: any) => handleError(e.response.data));
      } catch (e) {
        handleError(e);
      } finally {
        setRequiredDataLoaded(true);
      }
    };

    fetchData();
  }, []);

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.unknownPatient,
      errorUtils.doctorNotFound
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  };

  const handleChange = debounce(({values}: PatientPhysiotherapyReportFieldProps) => {
    setDoctorEmail(values.doctorEmail ? values.doctorEmail : "");
  })

  const openPatientPhysiotherapyReport = (reportResponse: PdfResponse) => {
    setPatientPhysiotherapyReportPdfResponse(reportResponse);
    setPatientPhysiotherapyReportModalOpen(true);
  }

  const closePatientPhysiotherapyReport = () => {
    setPatientPhysiotherapyReportModalOpen(false);
    setPatientPhysiotherapyReportPdfResponse(undefined);
  }

  const saveDocument = (successfullySaved: boolean) => {

    if (successfullySaved) {
      setSuccessfullySavedDocument(getMessage("physiotherapyReport.successfullySavedReport"));
    }

    closePatientPhysiotherapyReport();
    onCloseModal(true);
  }

  const sendEmail = (successfullySendEmail: boolean) => {

    if (successfullySendEmail) {
      setSuccessfullySendEmail(getMessage("physiotherapyReport.successfullySendEmail"));
    }

    closePatientPhysiotherapyReport();
    onCloseModal(true);
  }

  const getPatientDocumentPdfUrl = (pdfResponse: PdfResponse) => {
    return getPdfDocumentUrl(pdfResponse.pdfId, pdfResponse.pdfName);
  }

  const openPhysiotherapyReport = async (values: Partial<PatientPhysiotherapyReport>) => {

    const selectedDoctor = values.doctorInput;
    let doc = selectedDoctor;
    if (doctor && typeof selectedDoctor === "number") {
      const doctorDto = doctor as DoctorDto;
      doc = `${doctorDto.firstName} ${doctorDto.name}`;
    }

    const reportValues: Partial<PatientPhysiotherapyReport> = {
      ...values,
      doctor: doc as string,
      treaterId: options.currentTreater!.id,
      diagnosis: diagnoses.filter(d => d.selected),
      treatmentId: treatmentId
    };

    reportValues.treatmentGoals = [];
    if (values.selectedTreatmentGoals) {
      reportValues.treatmentGoals = Object.keys(values.selectedTreatmentGoals)
          .filter((tgKey: string) => values.selectedTreatmentGoals
              ? values.selectedTreatmentGoals[tgKey as PhysiotherapyTreatmentGoal]
              : false)
          .map((tgKey: string) => tgKey as PhysiotherapyTreatmentGoal);
    }

    const patientPhysiotherapyReportDto: PatientPhysiotherapyReportDto = {
      doctor: reportValues.doctor,
      doctorEmail: reportValues.doctorEmail,
      diagnosis: reportValues.diagnosis,
      medicine: reportValues.medicine,
      currentCondition: reportValues.currentCondition,
      comment: reportValues.comment,
      treatmentId: reportValues.treatmentId,
      treaterId: options.currentTreater.id,
      treatmentPrescription: reportValues.treatmentPrescription,
      treatmentGoals: reportValues.treatmentGoals,
      physiotherapeuticMeasures: reportValues.physiotherapeuticMeasures,
      treatmentsNumber: reportValues.treatmentsNumber,
      requiresAction: reportValues.requiresAction
    };

    setReportDto(patientPhysiotherapyReportDto);

    generatePatientPhysiotherapyReport(patient.id, reportValues, cancelTokenSource)
        .then(response => {
          openPatientPhysiotherapyReport(response);
        })
        .catch((e: any) => handleError(e.response.data));
  }

  const addNewDiagnose = (fields: any, index: number) => {

    const newDiagnose: ReportDiagnosisDtoWithId = {
      id: generateUUID(),
      treatmentDate: moment().format("YYYY-MM-DD"),
      code: "",
      description: "",
      editable: true,
      hidden: false,
      selected: true
    }

    fields.push(newDiagnose);

    setAddDiagnose(true);
    setAddDiagnoseIx(index);
  };

  const unhiddeDiagnosis = (form: FormApi) => {

    const unhiddenDiagnosis: ReportDiagnosisDtoWithId[] = diagnoses.map(d => {
      return {...d, hidden: false};
    })

    const modifiedDiagnoses = [...unhiddenDiagnosis];

    form.change("diagnosis", modifiedDiagnoses)

    setDiagnoses(modifiedDiagnoses);
  }

  const changeDiagnoses = (diagnoses: ReportDiagnosisDtoWithId[]) => {
    setDiagnoses(diagnoses);
  }

  const changeSelectedDiagnoses = (diagnoses: ReportDiagnosisDtoWithId[]) => {
    setSelectedDiagnoses(diagnoses);
  }

  const changeAddEditDiagnose = (addDiagnose: boolean) => {
    setAddDiagnose(addDiagnose);
  }

  const changeAddEditDiagnoseIx = (index?: number) => {
    setAddDiagnoseIx(index);
  }

  const renderDiagnosesSegment = (form: FormApi): JSX.Element => {

    return (
        <ReportDiagnosisComponent
            diagnoses={diagnoses}
            addDiagnose={addDiagnose}
            addDiagnoseIx={addDiagnoseIx}
            changeDiagnoses={changeDiagnoses}
            changeSelectedDiagnoses={changeSelectedDiagnoses}
            changeAddEditDiagnose={changeAddEditDiagnose}
            changeAddEditDiagnoseIx={changeAddEditDiagnoseIx}
            formChange={form.change}
        />
    );
  }

  const renderTreatmentPrescriptionSection = (): JSX.Element => {

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={5} style={{fontWeight: "bold"}}>
              {getMessage("physiotherapyReport.modal.physiotherapyPrescription")}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              {getMessage("physiotherapyReport.modal.physiotherapyPrescription.prescription")}
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="treatmentPrescription"
                  component={RadioGroup}
                  radioDefinitions={treatmentPrescription}
                  radiowidth="auto"
                  marginright="30px"
                  marginbottom="5px"
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  }

  const renderTreatmentGoalSection = (): JSX.Element => {

    const mappedFields = () => {
      const fieldNames = Object.values(PhysiotherapyTreatmentGoal);

      return fieldNames.map((fieldName, index) => (
          <Grid.Row key={index}>
            <Field
                name={`selectedTreatmentGoals[${fieldName}]`}
                component={CheckBox}
                label={getMessage(`enum.treatmentGoal.${fieldName}`)}
            />
          </Grid.Row>
      ))
    };

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8} style={{fontWeight: "bold"}}>
              {getMessage("physiotherapyReport.modal.treatmentGoal")}
            </Grid.Column>
            <Grid.Column width={8} style={{fontWeight: "bold"}}>
              {getMessage("physiotherapyReport.modal.physiotherapeuticMeasures")}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              {mappedFields()}
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  name="physiotherapeuticMeasures"
                  component={TextArea}
                  style={{minHeight: "267px", maxHeight: "267px", height: "267px"}}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  }

  const renderTreatmentsNumberSection = (): JSX.Element => {

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={3} style={{fontWeight: "bold"}}>
              {getMessage("physiotherapyReport.modal.treatmentsNumber")}
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="treatmentsNumber"
                  component={RadioGroup}
                  radioDefinitions={treatmentsNumber}
                  radiowidth="auto"
                  marginright="30px"
                  marginbottom="5px"
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  }

  const renderForm = ({form, submitting, handleSubmit}: FormRenderProps): JSX.Element => {

    const disableUnhiddeButton = diagnoses.filter(d => d.hidden).length === 0;

    return (
        <React.Fragment>
          <form onSubmit={handleSubmit}>
            {
                errorMessage
                && (
                    <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
            }
            {
                successfullySavedDocument
                && (
                    <Message success onDismiss={() => setSuccessfullySavedDocument(undefined)}>
                      {successfullySavedDocument}
                    </Message>
                )

            }
            {
                successfullySendEmail
                && (
                    <Message success onDismiss={() => setSuccessfullySendEmail(undefined)}>
                      {successfullySendEmail}
                    </Message>
                )

            }

            <FormContainer>
              <FormContent>
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("interventionOrderItem.reportNamePrefix")}:
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <Field
                          name="reportName"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <div style={{float: "right"}}>
                        <Field
                            name="requiresAction"
                            component={CheckBox}
                            label={getMessage("physiotherapyReport.modal.requiredAction")}
                        />
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("doctor")}:
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <Field
                          name="doctorInput"
                          component={DoctorSearchDropdown}
                          defaultValue={initialValues.current.doctor}
                          onDoctorChange={(doctor: DoctorDto | string | undefined) => {

                            setDoctor(doctor);

                            let email = "";
                            if (doctor && typeof doctor !== "string") {
                              email = (doctor as DoctorDto).email;
                            }
                            form.change("doctorEmail", email);
                          }}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("physiotherapyReport.modal.doctorEmail")}:
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <Field
                          name="doctorEmail"
                          component={Input}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={8} style={{fontWeight: "bold"}}>
                      {getMessage("physiotherapyReport.modal.diagnosis")}
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <Button
                          type="button"
                          compact
                          disabled={disableUnhiddeButton}
                          onClick={() => unhiddeDiagnosis(form)}
                          style={{float: "right"}}
                      >
                        {getMessage("synopsisReport.modal.unhiddeButton")}
                      </Button>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <div style={{width: "100%"}}>
                        {renderDiagnosesSegment(form)}
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <FieldArray name="diagnosis">
                        {({fields}) => (
                            <Button
                                type="button"
                                compact
                                style={addButton}
                                onClick={() => addNewDiagnose(fields, fields.length!)}
                            >
                              {getMessage('button.add')}
                            </Button>
                        )}
                      </FieldArray>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("physiotherapyReport.modal.medicine")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={10}>
                      <Field
                          name="medicine"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={3} style={{fontWeight: "bold"}}>
                      {getMessage("physiotherapyReport.modal.currentCondition")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={10}>
                      <Field
                          name="currentCondition"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {renderTreatmentPrescriptionSection()}
                  {renderTreatmentGoalSection()}
                  {renderTreatmentsNumberSection()}
                  <Grid.Row>
                    <Grid.Column width={2} style={{fontWeight: "bold"}}>
                      {getMessage("physiotherapyReport.modal.comment")}:
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={10}>
                      <Field
                          name="comment"
                          component={TextArea}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
              </FormContent>

              <FormButtonsRow>
                <Button compact
                        primary
                        disabled={submitting}
                >
                  {getMessage("physiotherapyReport.modal.generateReport.button")}
                </Button>
                <Button type="button"
                        compact
                        disabled={submitting}
                        onClick={() => onCloseModal(false)}>
                  {getMessage("button.cancel")}
                </Button>
              </FormButtonsRow>
            </FormContainer>

            <FormSpy subscription={{values: true}} onChange={handleChange}/>

            {
                patientPhysiotherapyReportModalOpen
                && <PatientReportPdfModal
                    pdfResponses={[patientPhysiotherapyReportPdfResponse]}
                    modalOpen={patientPhysiotherapyReportModalOpen}
                    onSave={saveDocument}
                    onSendEmail={sendEmail}
                    onCloseModal={closePatientPhysiotherapyReport}
                    getPdfUrl={getPatientDocumentPdfUrl}
                    sendEmailTextKey={getMessage("physiotherapyReport.pdfModal.email.send")}
                    doctorEmail={doctorEmail}
                    patient={patient}
                    pdfDocumentDescription={"Patient Physiotherapy Report"}
                    documentType={"PATIENT_PHYSIOTHERAPY_REPORT"}
                    treatmentId={treatmentId}
                    reportDto={reportDto}
                    reportId={documentId}
                />
            }
          </form>
        </React.Fragment>
    )
  }

  return (
      <Modal
          open={modalOpen}
          onClose={() => onCloseModal(false)}
          closeOnDimmerClick={false}
      >
        <Modal.Header>
          {getMessage("physiotherapyReport.modal.header") + " " + patient.name + " " + patient.firstName}
        </Modal.Header>
        <FormModalContentContainer>
          {!requiredDataLoaded
              ? <LoaderComponent message={getMessage("loading")}/>
              : <FinalForm
                  onSubmit={(values) => openPhysiotherapyReport(values)}
                  subscription={{submitting: true, pristine: true, values: true}}
                  initialValues={initialValues.current}
                  mutators={{
                    ...arrayMutators
                  }}
                  render={renderForm}
              />
          }
        </FormModalContentContainer>
      </Modal>
  );
};

export default withMessages(withAuthContext(UpsertPhysiotherapyReportModal));
